import { RootSelector, getStore } from '../../reducers';
import { ShareInProgress, SharingShape } from './model';
import { createSelector } from '@reduxjs/toolkit';
import { Asset, AssetPerm, isAssetDownloadAllowed, SharingLink } from '../../api';
import { Loading, Page } from '../finder/model';
import { getPage } from '../router/selectors';
import { getPermissions } from '../app/selectors';
import { Permission } from '../../../general/model';

export const getSharing: RootSelector<SharingShape> = createSelector(
  getStore,
  (state) => state.sharing,
);

export const getSharingId: RootSelector<SharingLink['id'] | undefined> = createSelector(
  getSharing,
  (sharing) => sharing.sharingId,
);

export const getData: RootSelector<SharingShape['data']> = createSelector(
  [getSharing],
  (sharing) => sharing.data,
);

export const getLoading: RootSelector<Loading> = createSelector(
  getSharing,
  (sharing) => sharing.loading,
);

export const getHasNextPage: RootSelector<boolean> = createSelector(
  getSharing,
  (sharing) => sharing.hasNextPage,
);

export const getScrollHash: RootSelector<string> = createSelector(
  getSharing,
  (sharing) => sharing.scrollHash,
);

export const getShareInProgress: RootSelector<ShareInProgress> = createSelector(
  getSharing,
  (sharing) => sharing.shareInProgress,
);

export const getIsPublicDownload: RootSelector<boolean> = createSelector(
  [getPage, getData],
  (page, data) => {
    return Boolean(
      page === Page.sharing && data.sharing_link && !data.sharing_link.is_safety_share,
    );
  },
);

// Ассеты, скачивание которых запрещено бэкендом (напр. шрифты под модерацией/отклонённые),
// не должны попадать в скачиваемый набор и в Bulk Download (MYUA-16456).
export const getPublicDownloadFilterFn = createSelector(
  [getIsPublicDownload],
  (isPublicDownload) => {
    return (assets: Asset[]) => {
      const downloadable = assets.filter(isAssetDownloadAllowed);
      return isPublicDownload
        ? downloadable
        : downloadable.filter(({ perms }) => perms.includes(AssetPerm.download));
    };
  },
);

export const getIsReplacingPack = createSelector(getSharing, (sharing) => sharing.isReplacingPack);

// Право создавать шаринг-ссылки. На демо-стенде пермишены отсутствуют —
// кнопки шаринга показываются задизейбленными с демо-тултипом.
export const getCanCreateSharingLink: RootSelector<boolean> = createSelector(
  getPermissions,
  (permissions) =>
    Boolean(
      permissions[Permission['fs.assetlab_create_public_sharing_link']] ||
        permissions[Permission['fs.assetlab_create_safety_sharing_link']],
    ),
);
